import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { type CardColor } from "@/lib/pals-data";
import { PLAYABLE_CARDS } from "@/lib/tcg-cards";
import { CardImage } from "@/components/CardImage";
import { breadcrumbSchema } from "@/lib/schema";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { ClusterLinks } from "@/components/ClusterLinks";


type CardKind = "pal" | "gear" | "structure" | "event";

interface AnyCard {
  kind: CardKind;
  id: string;
  name: string;
  subtitle: string;
  number: string;
  color: CardColor;
  cost: number;
  rarity: string;
  text: string;
  image: string;
  lucky: boolean;
}

const DECK_SIZE = 50;      // Main Deck — exactly 50 cards
const SOUL_DECK = 10;      // Soul Deck — exactly 10 Soul cards (built separately)
const MAX_COPIES = 4;      // up to 4 copies of any card name
const MAX_LUCKY = 8;       // up to 8 Lucky Pals per deck
const MAX_COLORS = 2;      // up to 2 colors, Colorless is free

export const Route = createFileRoute("/deck-builder")({
  head: () => ({
    meta: [
      { title: "Free Palworld TCG Deck Builder — 50-Card Decks" },
      { name: "description", content: "Free Palworld TCG deck builder, no sign-up: add Pal, Gear, Structure and Event cards with the 50-card deck, Soul deck and 4-copy limits checked for you." },
      { property: "og:title", content: "Palworld TCG Deck Builder — Pal Stars" },
      { property: "og:description", content: "Build a legal 50-card Palworld TCG deck with live cost curve, color balance and export." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/deck-builder" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld TCG Deck Builder — Pal Stars" },
      { name: "twitter:description", content: "Build a legal 50-card Palworld TCG deck with live cost curve and export." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/deck-builder" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Deck Builder", "/deck-builder"]]),
{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to build a Palworld TCG deck",
        description: "Build a legal 50-card Palworld Official Card Game deck using Pal, Gear, Structure and Event cards.",
        step: [
          { "@type": "HowToStep", name: "Pick a color identity", text: "Choose one or two of Red, Blue, Green, Purple or Colorless to focus your deck." },
          { "@type": "HowToStep", name: "Add 24-28 Pal cards", text: "Spread costs across 1 to 6 souls to keep an even curve in a 50-card main deck." },
          { "@type": "HowToStep", name: "Add Gear, Structures and Events", text: "Round out the deck with support cards — usually 18-24 non-Pal cards." },
          { "@type": "HowToStep", name: "Respect the deck limits", text: "Up to four copies of any card name and no more than eight Lucky Pals." },
          { "@type": "HowToStep", name: "Build the 10-card Soul deck", text: "Your Soul deck is a separate pile of exactly ten Soul cards used to pay costs." },
          { "@type": "HowToStep", name: "Export your list", text: "Copy the deck list and share it or import into your play group's tracker." },
        ],
      }),
    }],
  }),
  component: DeckBuilder,
});

const COLOR_TONE: Record<CardColor, string> = {
  Red: "border-red-500/40 text-red-300",
  Blue: "border-sky-500/40 text-sky-300",
  Green: "border-emerald-500/40 text-emerald-300",
  Purple: "border-violet-500/40 text-violet-300",
  Colorless: "border-slate-400/40 text-slate-300",
};

/**
 * Deck pool = only cards that are actually legal in Palworld OFFICIAL CARD GAME
 * play: the printed Booster Pack and Trial Deck Pal / Gear / Structure / Event
 * cards, one entry per collector number (parallels share a base card, and Soul
 * cards plus deck accessories are not deck-list cards).
 */
function buildPool(): AnyCard[] {
  const KIND: Record<string, CardKind> = { Pal: "pal", Gear: "gear", Structure: "structure", Event: "event" };
  return PLAYABLE_CARDS.filter((c) => KIND[c.type]).map((c): AnyCard => ({
    kind: KIND[c.type],
    id: c.code,
    name: c.name,
    subtitle: c.subtitle,
    number: c.code,
    color: c.color as CardColor,
    cost: c.cost ?? 0,
    rarity: c.rarity,
    text: c.effect,
    image: c.image,
    lucky: Boolean(c.lucky),
  }));
}

function encodeDeck(deck: Record<string, number>): string {
  const parts = Object.entries(deck).filter(([, n]) => n > 0).map(([id, n]) => `${id}:${n}`);
  return parts.join(",");
}
function decodeDeck(s: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of s.split(",")) {
    const [id, n] = p.split(":");
    const count = Math.min(MAX_COPIES, Math.max(0, parseInt(n || "0", 10) || 0));
    if (id && count > 0) out[id] = count;
  }
  return out;
}

function DeckBuilder() {
  const pool = useMemo(buildPool, []);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<"all" | AnyCard["kind"]>("all");
  const [color, setColor] = useState<"all" | CardColor>("all");
  const [deck, setDeck] = useState<Record<string, number>>(() => {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search);
    const d = params.get("d");
    return d ? decodeDeck(d) : {};
  });
  const [shareMsg, setShareMsg] = useState<string>("");
  const [importText, setImportText] = useState<string>("");
  const [importErr, setImportErr] = useState<string>("");

  function importDeck() {
    setImportErr("");
    const raw = importText.trim();
    if (!raw) return;
    let payload = raw;
    // Accept either a full share URL or just the encoded string
    try {
      if (/^https?:\/\//i.test(raw)) {
        const u = new URL(raw);
        const d = u.searchParams.get("d");
        if (!d) { setImportErr("No ?d= deck code found in that URL."); return; }
        payload = d;
      }
    } catch { setImportErr("Could not parse that URL."); return; }

    let decoded: Record<string, number> = {};
    if (/^\s*\{/.test(payload)) {
      // JSON export from this page
      try {
        const parsed = JSON.parse(payload) as { cards?: { code: string; count: number }[] };
        for (const c of parsed.cards ?? []) if (c?.code) decoded[c.code.toUpperCase()] = (decoded[c.code.toUpperCase()] ?? 0) + (c.count || 0);
      } catch { setImportErr("That JSON could not be parsed."); return; }
    } else if (/\n/.test(payload) || /\d+\s*[x×]\s*E/i.test(payload)) {
      // Plain text list: "4x EBP01-001 — Jormuntide Ignis"
      for (const line of payload.split(/\n+/)) {
        const m = line.match(/(\d+)\s*[x×]?\s*(E[A-Z0-9]+-[A-Z0-9]+)/i);
        if (m) decoded[m[2].toUpperCase()] = (decoded[m[2].toUpperCase()] ?? 0) + parseInt(m[1], 10);
      }
    } else {
      decoded = decodeDeck(payload);
    }
    const valid: Record<string, number> = {};
    let total = 0;
    for (const [id, n] of Object.entries(decoded)) {
      if (!pool.some((c) => c.id === id)) continue;
      const capped = Math.min(MAX_COPIES, n);
      if (total + capped > DECK_SIZE) { valid[id] = Math.max(0, DECK_SIZE - total); total = DECK_SIZE; break; }
      valid[id] = capped; total += capped;
    }
    if (!Object.keys(valid).length) { setImportErr("No recognised cards in that code."); return; }
    setDeck(valid);
    setImportText("");
    setShareMsg(`Imported ${total} cards`);
    setTimeout(() => setShareMsg(""), 2500);
  }

  // Keep URL in sync with deck for shareable links
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const encoded = encodeDeck(deck);
    if (encoded) url.searchParams.set("d", encoded);
    else url.searchParams.delete("d");
    window.history.replaceState({}, "", url.toString());
  }, [deck]);


  const totalCards = useMemo(() => Object.values(deck).reduce((a, b) => a + b, 0), [deck]);
  const byId = useMemo(() => new Map(pool.map((c) => [c.id, c] as const)), [pool]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return pool.filter((c) => {
      if (kind !== "all" && c.kind !== kind) return false;
      if (color !== "all" && c.color !== color) return false;
      if (!needle) return true;
      return `${c.name} ${c.number} ${c.text}`.toLowerCase().includes(needle);
    }).slice(0, 120);
  }, [pool, q, kind, color]);

  function add(id: string) {
    setDeck((d) => {
      const cur = d[id] ?? 0;
      if (cur >= MAX_COPIES) return d;
      if (totalCards >= DECK_SIZE) return d;
      return { ...d, [id]: cur + 1 };
    });
  }
  function remove(id: string) {
    setDeck((d) => {
      const cur = d[id] ?? 0;
      if (cur <= 1) { const { [id]: _, ...rest } = d; return rest; }
      return { ...d, [id]: cur - 1 };
    });
  }

  const deckList = useMemo(() => Object.entries(deck).map(([id, n]) => ({ card: byId.get(id)!, n })).filter((r) => r.card), [deck, byId]);
  const curve = useMemo(() => {
    const buckets: Record<string, number> = { "1": 0, "2": 0, "3": 0, "4": 0, "5+": 0 };
    for (const { card, n } of deckList) {
      const k = card.cost >= 5 ? "5+" : String(Math.max(1, card.cost));
      buckets[k] = (buckets[k] ?? 0) + n;
    }
    return buckets;
  }, [deckList]);
  const colors = useMemo(() => {
    const m: Record<CardColor, number> = { Red: 0, Blue: 0, Green: 0, Purple: 0, Colorless: 0 };
    for (const { card, n } of deckList) m[card.color] += n;
    return m;
  }, [deckList]);

  const luckyCount = useMemo(() => deckList.reduce((a, { card, n }) => a + (card.lucky ? n : 0), 0), [deckList]);
  const colorsUsed = useMemo(
    () => (["Red","Blue","Green","Purple"] as CardColor[]).filter((c) => colors[c] > 0),
    [colors],
  );
  const violations = useMemo(() => {
    const v: string[] = [];
    if (totalCards !== DECK_SIZE) v.push(`Main deck must be exactly ${DECK_SIZE} cards (currently ${totalCards}).`);
    if (luckyCount > MAX_LUCKY) v.push(`Too many Lucky Pals: ${luckyCount}/${MAX_LUCKY}.`);
    if (colorsUsed.length > MAX_COLORS) v.push(`Too many colors: ${colorsUsed.join(", ")} — max ${MAX_COLORS} (Colorless is free).`);
    return v;
  }, [totalCards, luckyCount, colorsUsed]);

  const legal = violations.length === 0 && Object.values(deck).every((n) => n <= MAX_COPIES);

  function deckText() {
    const lines = deckList
      .sort((a, b) => a.card.cost - b.card.cost || a.card.name.localeCompare(b.card.name))
      .map(({ card, n }) => `${n}x ${card.number} — ${card.name} (${card.color}, cost ${card.cost})`);
    return `Pal Stars — Palworld TCG Deck (${totalCards}/${DECK_SIZE} main + ${SOUL_DECK} Soul)\n\n${lines.join("\n")}\n`;
  }

  function copyList() {
    navigator.clipboard?.writeText(deckText()).catch(() => {});
    setShareMsg("Deck list copied");
    setTimeout(() => setShareMsg(""), 2500);
  }

  function download(name: string, body: string, type: string) {
    if (typeof window === "undefined") return;
    const url = URL.createObjectURL(new Blob([body], { type }));
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setShareMsg(`Exported ${name}`);
    setTimeout(() => setShareMsg(""), 2500);
  }

  function exportTxt() { download("palstars-deck.txt", deckText(), "text/plain"); }
  function exportJson() {
    const payload = {
      format: "palstars-deck-v1",
      game: "Palworld OFFICIAL CARD GAME",
      mainDeckSize: DECK_SIZE,
      soulDeckSize: SOUL_DECK,
      total: totalCards,
      cards: deckList.map(({ card, n }) => ({ code: card.number, name: card.name, color: card.color, cost: card.cost, count: n })),
    };
    download("palstars-deck.json", JSON.stringify(payload, null, 2), "application/json");
  }

  async function shareLink() {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const encoded = encodeDeck(deck);
    if (encoded) url.searchParams.set("d", encoded); else url.searchParams.delete("d");
    const href = url.toString();
    try {
      if (navigator.share) {
        await navigator.share({ title: "My Palworld TCG deck — Pal Stars", url: href });
        setShareMsg("Shared!");
      } else {
        await navigator.clipboard.writeText(href);
        setShareMsg("Share link copied to clipboard");
      }
    } catch {
      setShareMsg("Could not share — copy the URL manually.");
    }
    setTimeout(() => setShareMsg(""), 2500);
  }

  /**
   * Deck snapshot: a short, readable summary of the deck — size, legality,
   * color split, curve and top cards — plus the share link, so it can be
   * pasted straight into Discord or a social post.
   */
  function snapshotText() {
    if (typeof window === "undefined") return "";
    const url = new URL(window.location.href);
    const encoded = encodeDeck(deck);
    if (encoded) url.searchParams.set("d", encoded); else url.searchParams.delete("d");
    const colorLine = (["Red", "Blue", "Green", "Purple", "Colorless"] as CardColor[])
      .filter((c) => colors[c] > 0)
      .map((c) => `${c} ${colors[c]}`)
      .join(" · ");
    const curveLine = Object.entries(curve).map(([k, v]) => `${k}:${v}`).join(" ");
    const top = deckList
      .slice()
      .sort((a, b) => b.n - a.n || a.card.name.localeCompare(b.card.name))
      .slice(0, 5)
      .map(({ card, n }) => `${n}× ${card.name} (${card.number})`)
      .join(", ");
    return [
      `📸 Palworld TCG deck snapshot — Pal Stars`,
      `${totalCards}/${DECK_SIZE} main + ${SOUL_DECK} Soul · ${legal ? "legal ✅" : "not legal yet ⚠️"}`,
      colorLine ? `Colors: ${colorLine}` : "",
      `Curve: ${curveLine}`,
      top ? `Top cards: ${top}` : "",
      `Open the full list: ${url.toString()}`,
    ].filter(Boolean).join("\n");
  }

  async function shareSnapshot() {
    const text = snapshotText();
    if (!text) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: "My Palworld TCG deck snapshot — Pal Stars", text });
        setShareMsg("Snapshot shared!");
      } else {
        await navigator.clipboard.writeText(text);
        setShareMsg("Deck snapshot copied — paste it anywhere");
      }
    } catch {
      setShareMsg("Could not share the snapshot — copy the deck list instead.");
    }
    setTimeout(() => setShareMsg(""), 2500);
  }


  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">Home</Link> /{" "}
        <Link to="/cards" className="hover:text-foreground">Cards</Link> /{" "}
        <span className="text-foreground">Deck Builder</span>
      </nav>

      <h1 className="text-3xl sm:text-4xl font-bold">Palworld TCG Deck Builder</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Build a legal 50-card Palworld Official Card Game deck. Pick from every Pal, Gear, Structure and Event card in
        the Pal Stars database. Max 4 copies per card. Live curve, color balance and one-click export.
      </p>

      <aside className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Quick answer</div>
        A legal Palworld OFFICIAL CARD GAME deck is a <b>50-card main deck</b> plus a separate <b>10-card Soul deck</b>,
        with at most <b>4 copies</b> of any card name, up to <b>8 Lucky Pals</b> and no more than <b>2 colors</b>
        (Colorless is always free). Aim for ~24–28 Pals plus ~22 Gear / Structure / Event cards.
      </aside>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
        <section>
          <div className="flex flex-wrap gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cards…" aria-label="Search cards" className="min-w-[220px] flex-1 rounded-lg glass px-4 py-2 text-sm outline-none focus:ring-2 ring-primary" />
            <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} aria-label="Filter by card type" className="rounded-lg glass px-3 py-2 text-sm">
              <option value="all">All types</option>
              <option value="pal">Pal</option>
              <option value="gear">Gear</option>
              <option value="structure">Structure</option>
              <option value="event">Event</option>
            </select>
            <select value={color} onChange={(e) => setColor(e.target.value as typeof color)} aria-label="Filter by card color" className="rounded-lg glass px-3 py-2 text-sm">
              <option value="all">All colors</option>
              <option>Red</option><option>Blue</option><option>Green</option><option>Purple</option><option>Colorless</option>
            </select>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((c) => {
              const owned = deck[c.id] ?? 0;
              return (
                <article key={c.id} className={`glass rounded-xl p-3 border ${COLOR_TONE[c.color]}`}>
                  <div className="flex items-start gap-3">
                    {c.image && (
                      <CardImage
                        src={c.image}
                        alt={`Palworld TCG card ${c.number} — ${c.name}`}
                        width={56}
                        height={78}
                        className="shrink-0 rounded-md"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] text-muted-foreground">{c.number} · {c.rarity} · {c.kind}</div>
                      <h2 className="font-semibold truncate">{c.name}</h2>
                      {c.subtitle && <div className="text-[10px] text-muted-foreground truncate">{c.subtitle}</div>}
                    </div>
                    <span className="rounded-full border px-2 py-0.5 text-[10px] shrink-0">Cost {c.cost}</span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{c.text}</p>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">In deck: {owned}/{MAX_COPIES}</span>
                    <div className="flex gap-1">
                      <button onClick={() => remove(c.id)} disabled={!owned} className="rounded-md border px-2 py-1 disabled:opacity-40 hover:bg-card">−</button>
                      <button onClick={() => add(c.id)} disabled={owned >= MAX_COPIES || totalCards >= DECK_SIZE} className="rounded-md border px-2 py-1 disabled:opacity-40 hover:bg-card">+</button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <aside className="lg:sticky lg:top-20 h-fit glass rounded-2xl p-5">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold">Your deck</h2>
            <span className={`text-sm ${legal ? "text-emerald-400" : "text-muted-foreground"}`}>{totalCards}/{DECK_SIZE}{legal ? " · Legal" : ""}</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 text-[10px] text-muted-foreground">
            <span>Soul deck {SOUL_DECK} cards (separate)</span>
            <span className={luckyCount > MAX_LUCKY ? "text-red-400" : ""}>Lucky {luckyCount}/{MAX_LUCKY}</span>
            <span className={colorsUsed.length > MAX_COLORS ? "text-red-400" : ""}>Colors {colorsUsed.length}/{MAX_COLORS}</span>
          </div>
          {violations.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-[10px] text-amber-400">
              {violations.map((v) => <li key={v}>• {v}</li>)}
            </ul>
          )}

          <div className="mt-3 grid grid-cols-5 gap-1 text-[10px]">
            {(Object.keys(curve) as (keyof typeof curve)[]).map((k) => (
              <div key={k} className="rounded border bg-background/40 p-1.5 text-center">
                <div className="text-muted-foreground">Cost {k}</div>
                <div className="font-semibold text-sm">{curve[k]}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-5 gap-1 text-[10px]">
            {(["Red","Blue","Green","Purple","Colorless"] as CardColor[]).map((c) => (
              <div key={c} className="rounded border bg-background/40 p-1.5 text-center">
                <div className="text-muted-foreground">{c}</div>
                <div className="font-semibold text-sm">{colors[c]}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 max-h-80 overflow-auto rounded-lg border bg-background/40">
            {deckList.length === 0 && <div className="p-3 text-xs text-muted-foreground">Empty deck. Add cards from the pool.</div>}
            {deckList.map(({ card, n }) => (
              <div key={card.id} className="flex items-center justify-between gap-2 border-b last:border-0 px-3 py-2 text-xs">
                <div className="min-w-0">
                  <div className="truncate font-medium">{n}× {card.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{card.number} · {card.color} · cost {card.cost}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => remove(card.id)} className="rounded border px-1.5 py-0.5 hover:bg-card">−</button>
                  <button onClick={() => add(card.id)} disabled={n >= MAX_COPIES || totalCards >= DECK_SIZE} className="rounded border px-1.5 py-0.5 disabled:opacity-40 hover:bg-card">+</button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={copyList} disabled={!deckList.length} className="flex-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">
              Copy deck list
            </button>
            <button onClick={exportTxt} disabled={!deckList.length} className="rounded-lg border px-3 py-2 text-sm disabled:opacity-40 hover:bg-card">Export .txt</button>
            <button onClick={exportJson} disabled={!deckList.length} className="rounded-lg border px-3 py-2 text-sm disabled:opacity-40 hover:bg-card">Export .json</button>
            <button onClick={shareLink} disabled={!deckList.length} className="rounded-lg border border-primary/50 bg-primary/10 px-3 py-2 text-sm text-primary font-semibold disabled:opacity-40 hover:bg-primary/20">
              Share link
            </button>
            <button onClick={shareSnapshot} disabled={!deckList.length} className="rounded-lg border border-primary/50 bg-primary/10 px-3 py-2 text-sm text-primary font-semibold disabled:opacity-40 hover:bg-primary/20">
              Share snapshot
            </button>
            <button onClick={() => setDeck({})} disabled={!deckList.length} className="rounded-lg border px-3 py-2 text-sm disabled:opacity-40 hover:bg-card">Clear</button>
          </div>
          {shareMsg && <p className="mt-2 text-[11px] text-emerald-400" aria-live="polite">{shareMsg}</p>}
          <p className="mt-2 text-[10px] text-muted-foreground">Share link encodes your deck in the URL — paste anywhere to let others open the exact list. “Share snapshot” copies a short readable summary (size, legality, colors, curve, top cards) with that link.</p>
          {deckList.length > 0 && (
            <details className="mt-2 rounded-lg border bg-background/40 p-3">
              <summary className="cursor-pointer text-[11px] font-semibold text-primary">Preview deck snapshot</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words text-[10px] text-muted-foreground">{snapshotText()}</pre>
            </details>
          )}


          <div className="mt-4 rounded-lg border bg-background/40 p-3">
            <label htmlFor="deck-import" className="text-[10px] uppercase tracking-widest text-primary">Import deck</label>
            <p className="mt-1 text-[10px] text-muted-foreground">Paste a share link, deck code, exported JSON, or a text list like “4x EBP01-001”. You can also load a .txt / .json export.</p>
            <div className="mt-2 flex gap-2">
              <input
                id="deck-import"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                aria-label="Paste a deck share link, deck code or card list to import"
                placeholder="https://palstars.com/deck-builder?d=…"
                className="flex-1 min-w-0 rounded-md border bg-background/60 px-2 py-1.5 text-xs outline-none focus:ring-2 ring-primary"
              />
              <button onClick={importDeck} disabled={!importText.trim()} className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-40">Import</button>
            </div>
            <label className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground">
              <span className="cursor-pointer rounded-md border px-2 py-1 hover:bg-card">Load file…</span>
              <input
                type="file"
                accept=".txt,.json,text/plain,application/json"
                aria-label="Import a deck file"
                className="sr-only"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setImportText(await f.text());
                  e.target.value = "";
                }}
              />
            </label>
            {importErr && <p className="mt-1 text-[10px] text-red-400">{importErr}</p>}
          </div>
        </aside>
      </div>

      <section className="mt-12 rounded-2xl border bg-card/40 p-6">
        <h2 className="text-lg font-semibold">Deck building tips</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li>• Keep 24–28 Pal cards and 22–26 support cards in the 50-card main deck.</li>
          <li>• Curve to 4 cost. Cards costing 5+ should be limited to your finishers.</li>
          <li>• Two colors max (Colorless is free) — a third color is not legal.</li>
          <li>• Include 4× your win-condition Pal (e.g. Eidrolon, Jetragon, Anubis).</li>
          <li>• Structures buff work speed on-board — pair with Gear that protects them.</li>
          <li>• Events are your interaction: keep 8–10 to answer opposing threats.</li>
          <li>• Your Soul deck is a separate 10-card pile — you reveal Souls to pay costs each turn.</li>
        </ul>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <Link to="/cards" className="rounded-full border px-3 py-1.5 hover:bg-card">Browse the card database</Link>
          <Link to="/guides/$slug" params={{ slug: "tcg" }} className="rounded-full border px-3 py-1.5 hover:bg-card">Palworld TCG Guide</Link>
          <Link to="/eidrolon" className="rounded-full border px-3 py-1.5 hover:bg-card">Eidrolon wiki</Link>
        </div>
      </section>

      <InternalLinkBlock path="/deck-builder" />
      <ClusterLinks hub="/deck-builder" heading="Palworld TCG deck building guides" />
    </div>
  );
}
